/* eslint-disable @repo/no-style-props */
import Header from "@/src/components/layouts/header";
import { Button, type ButtonProps } from "@/src/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/src/components/ui/drawer";
import { CommentList } from "@/src/features/comments/CommentList";
import { useHasProjectAccess } from "@/src/features/rbac/utils/checkProjectAccess";
import { type CommentObjectType } from "@langfuse/shared";
// LFE-7628: general (trace/observation/session) comments use a square speech
// bubble to stay visually distinct from per-score comments, which use the round
// MessageCircle bubble in the annotation form.
import { MessageSquare, MessageSquareOff } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { type SelectionData } from "./contexts/InlineCommentSelectionContext";

export function CommentDrawerButton({
  projectId,
  objectId,
  objectType,
  count,
  variant = "secondary",
  className,
  size = "default",
  pendingSelection,
  onSelectionUsed,
  onCommentChange,
  isOpen: controlledIsOpen,
  onOpenChange: controlledOnOpenChange,
  layout = "toolbar",
}: {
  projectId: string;
  objectId: string;
  objectType: CommentObjectType;
  count?: number;
  variant?: ButtonProps["variant"];
  className?: string;
  size?: ButtonProps["size"];
  pendingSelection?: SelectionData | null;
  onSelectionUsed?: () => void;
  onCommentChange?: () => void | Promise<void>;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * "toolbar" (default) is the inline button; "menu" renders the same drawer
   * trigger as a full-width labeled row for the mobile header overflow popover.
   */
  layout?: "toolbar" | "menu";
}) {
  const isMenu = layout === "menu";
  const router = useRouter();
  const [isMentionDropdownOpen, setIsMentionDropdownOpen] = useState(false);
  const [internalIsDrawerOpen, setInternalIsDrawerOpen] = useState(false);
  const hasAutoOpenedRef = useRef(false); // Track if we've already auto-opened for current deep link

  const isDrawerOpen = controlledIsOpen ?? internalIsDrawerOpen;
  const setIsDrawerOpen = controlledOnOpenChange ?? setInternalIsDrawerOpen;
  const hasFocusedRef = useRef(false); // Track if we've already focused the drawer

  const hasReadAccess = useHasProjectAccess({
    projectId,
    scope: "comments:read",
  });
  const hasWriteAccess = useHasProjectAccess({
    projectId,
    scope: "comments:CUD",
  });

  // Auto-open drawer when comments=open query param is present AND matches this drawer's object
  useEffect(() => {
    const shouldAutoOpen =
      router.query.comments === "open" &&
      router.query.commentObjectType === objectType &&
      router.query.commentObjectId === objectId &&
      hasReadAccess &&
      !isDrawerOpen &&
      !hasAutoOpenedRef.current;

    // Only open if drawer is not already open AND we haven't auto-opened yet for this deep link
    if (shouldAutoOpen) {
      hasAutoOpenedRef.current = true;
      setIsDrawerOpen(true);

      // Scroll to specific comment if hash is present
      if (router.asPath.includes("#comment-")) {
        // Wait for drawer animation to complete before scrolling
        setTimeout(() => {
          const hash = router.asPath.split("#")[1];
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 300);
      }
    }

    // Reset the flag when query params are cleared (user navigated away from deep link)
    if (router.query.comments !== "open" && hasAutoOpenedRef.current) {
      hasAutoOpenedRef.current = false;
    }
  }, [
    router.query.comments,
    router.query.commentObjectType,
    router.query.commentObjectId,
    router.asPath,
    hasReadAccess,
    objectType,
    objectId,
    isDrawerOpen,
    setIsDrawerOpen,
  ]);

  if (!hasReadAccess || (!hasWriteAccess && !count))
    return (
      <Button
        type="button"
        variant={isMenu ? "ghost" : "secondary"}
        size={isMenu ? "sm" : size}
        className={
          isMenu ? "w-full justify-start gap-2 font-normal" : className
        }
        disabled
      >
        <MessageSquareOff
          className={
            isMenu
              ? "text-muted-foreground h-4 w-4"
              : size === "sm"
                ? "text-muted-foreground h-3.5 w-3.5"
                : "text-muted-foreground h-4 w-4"
          }
        />
        {isMenu ? <span className="text-sm">Add comment</span> : null}
      </Button>
    );

  return (
    <Drawer
      open={isDrawerOpen}
      onOpenChange={(open) => {
        // Prevent drawer from closing when mention dropdown is open
        if (!open && isMentionDropdownOpen) {
          // Keep drawer open
          return;
        }
        setIsDrawerOpen(open);

        // Reset focus tracking when drawer closes
        if (!open) {
          hasFocusedRef.current = false;
        }

        // Clear URL parameters and hash when drawer is closed
        if (!open && router.query.comments === "open") {
          const { comments, commentObjectType, commentObjectId, ...rest } =
            router.query;
          router.replace(
            {
              pathname: router.pathname,
              query: rest,
            },
            undefined,
            { shallow: true },
          );
        }
      }}
    >
      <DrawerTrigger asChild>
        <Button
          type="button"
          variant={isMenu ? "ghost" : variant}
          size={isMenu ? "sm" : size}
          className={
            isMenu ? "w-full justify-start gap-2 font-normal" : className
          }
          id="comment-drawer-button"
        >
          <div
            className={
              isMenu ? "flex items-center gap-2" : "flex items-center gap-1"
            }
          >
            <MessageSquare
              className={
                isMenu ? "h-4 w-4" : size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4"
              }
            />
            <span className={isMenu ? "text-sm" : undefined}>Add comment</span>
            {!!count ? (
              <span className="bg-primary/50 text-primary-foreground flex h-3.5 w-fit items-center justify-center rounded-sm px-1 text-xs shadow-xs">
                {count > 99 ? "99+" : count}
              </span>
            ) : null}
          </div>
        </Button>
      </DrawerTrigger>
      <DrawerContent
        overlayClassName="bg-primary/10"
        className="h-screen-with-banner max-h-screen-with-banner overflow-hidden"
      >
        <div
          className="mx-auto flex h-full w-full flex-col overflow-hidden focus:ring-0 focus:outline-hidden focus-visible:ring-0 focus-visible:outline-hidden md:max-h-full"
          tabIndex={-1}
          ref={(el) => {
            // Auto-focus drawer content when it opens (only once)
            if (el && isDrawerOpen && !hasFocusedRef.current) {
              hasFocusedRef.current = true;
              setTimeout(() => el.focus({ preventScroll: true }), 100);
            }
          }}
        >
          <DrawerHeader className="bg-background sr-only shrink-0 rounded-sm">
            <DrawerTitle>
              <Header title="Comments"></Header>
            </DrawerTitle>
          </DrawerHeader>
          <div
            data-vaul-no-drag
            className="min-h-0 flex-1 overflow-hidden px-2 py-2"
          >
            <CommentList
              projectId={projectId}
              objectId={objectId}
              objectType={objectType}
              onMentionDropdownChange={setIsMentionDropdownOpen}
              isDrawerOpen={isDrawerOpen}
              pendingSelection={pendingSelection}
              onSelectionUsed={onSelectionUsed}
              onCommentChange={onCommentChange}
            />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
